<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { parsePuiTxtContent, type IPuiTxtRecord } from '@shared/modules/parsePuiTxt'
import { ECodigoError, CCodigoError } from '@shared/types/pui/enums'

const getCodigoErrorMensaje = (codigo: string): string => CCodigoError[codigo as ECodigoError] ?? codigo

const recordMatchesBusqueda = (record: IPuiTxtRecord, term: string): boolean =>
	record.nombreCompleto.toLowerCase().includes(term) ||
	record.curp.toLowerCase().includes(term) ||
	record.matricula.toLowerCase().includes(term) ||
	record.codigosError.some(codigo => {
		const codigoLower = codigo.toLowerCase()
		const mensajeLower = getCodigoErrorMensaje(codigo).toLowerCase()
		return codigoLower.includes(term) || mensajeLower.includes(term)
	})

const archivoTxt = ref<File[]>([])
const processingTxt = ref(false)
const parseEjecutado = ref(false)
const error = ref<string | null>(null)
const showErrorModal = ref(false)
const parseWarnings = ref<string[]>([])
const registros = ref<IPuiTxtRecord[]>([])
const hasHeader = ref(false)
const isErrorFile = ref(false)
const selectedRecord = ref<IPuiTxtRecord | null>(null)

const tieneArchivo = computed(() => archivoTxt.value.length > 0)
const nombreArchivo = computed(() => archivoTxt.value[0]?.name ?? '')
const registrosConError = computed(() => registros.value.filter(r => r.tieneError).length)
const busqueda = ref('')

const registrosFiltrados = computed(() => {
	const term = busqueda.value.trim().toLowerCase()
	if (!term) return registros.value

	return registros.value.filter(record => recordMatchesBusqueda(record, term))
})

const readFileAsText = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			if (typeof reader.result !== 'string') {
				reject(new Error('No se pudo leer el archivo TXT'))
				return
			}
			resolve(reader.result)
		}
		reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo TXT'))
		reader.readAsText(file, 'UTF-8')
	})

const procesarArchivo = async () => {
	const file = archivoTxt.value[0]
	if (!file) return

	processingTxt.value = true
	error.value = null
	parseWarnings.value = []
	registros.value = []
	parseEjecutado.value = false
	hasHeader.value = false
	isErrorFile.value = false

	try {
		const content = await readFileAsText(file)
		const result = parsePuiTxtContent(content)

		hasHeader.value = result.hasHeader
		isErrorFile.value = result.isErrorFile
		parseWarnings.value = result.parseErrors
		registros.value = result.records
		parseEjecutado.value = true

		if (result.records.length === 0) {
			error.value = result.parseErrors[0] ?? 'El archivo no contiene registros para mostrar.'
			showErrorModal.value = true
		}
	} catch (err: unknown) {
		console.error('Error al procesar TXT PUI:', err)
		error.value = err instanceof Error ? err.message : 'Error al procesar el archivo TXT'
		showErrorModal.value = true
	} finally {
		archivoTxt.value = []
		processingTxt.value = false
	}
}

const limpiarArchivo = () => {
	archivoTxt.value = []
	registros.value = []
	parseWarnings.value = []
	parseEjecutado.value = false
	hasHeader.value = false
	isErrorFile.value = false
	busqueda.value = ''
	selectedRecord.value = null
	error.value = null
}
</script>

<template>
	<div class="validador-grid">
		<aside class="validador-panel validador-panel--info">
			<div class="validador-panel__header">
				<VaIcon name="info" class="validador-panel__icon" />
				<h2 class="validador-panel__title">¿Qué hace esta herramienta?</h2>
			</div>
			<p class="validador-panel__text">Convierte archivos TXT de la PUI (insumo generado o respuesta de calidad de datos DQ) en registros estructurados para revisarlos de forma clara.</p>
			<ul class="validador-steps">
				<li class="validador-step">
					<span class="validador-step__num">1</span>
					<span>Carga el archivo TXT devuelto por la plataforma o generado por Certisep.</span>
				</li>
				<li class="validador-step">
					<span class="validador-step__num">2</span>
					<span>Procesa el archivo para separar cada línea en un objeto con sus campos.</span>
				</li>
				<li class="validador-step">
					<span class="validador-step__num">3</span>
					<span>Consulta la tabla resumida y abre el detalle de cada registro.</span>
				</li>
			</ul>
		</aside>

		<section class="validador-panel validador-panel--upload">
			<div class="validador-panel__header">
				<VaIcon name="upload_file" class="validador-panel__icon" />
				<div>
					<h2 class="validador-panel__title">Cargar archivo TXT</h2>
					<p class="validador-panel__hint">Formato: .txt delimitado por pipe (|)</p>
				</div>
			</div>

			<VaFileUpload
				v-model="archivoTxt"
				dropzone
				color="primary"
				fileTypes=".txt,text/plain"
				uploadButtonText="Seleccionar TXT"
				dropZoneText="Arrastra tu archivo aquí o haz clic para seleccionar"
				class="validador-upload" />

			<div v-if="tieneArchivo" class="validador-file-badge">
				<VaIcon name="insert_drive_file" color="success" size="small" />
				<span class="validador-file-badge__name">{{ nombreArchivo }}</span>
			</div>

			<div class="validador-actions">
				<VaButton color="primary" icon="transform" :disabled="!tieneArchivo || processingTxt" @click="procesarArchivo"> Procesar archivo </VaButton>
				<VaButton preset="secondary" icon="delete" :disabled="!tieneArchivo || processingTxt" @click="limpiarArchivo"> Limpiar </VaButton>
			</div>
		</section>
	</div>

	<div v-if="parseEjecutado" class="validador-stats">
		<div class="validador-stat">
			<span class="validador-stat__value">{{ registros.length }}</span>
			<span class="validador-stat__label">Registros encontrados</span>
		</div>
		<div class="validador-stat" :class="{ 'validador-stat--warn': registrosConError > 0 }">
			<span class="validador-stat__value">{{ registrosConError }}</span>
			<span class="validador-stat__label">Con error DQ</span>
		</div>
		<div class="validador-stat">
			<span class="validador-stat__value">{{ hasHeader ? 'Sí' : 'No' }}</span>
			<span class="validador-stat__label">Encabezado detectado</span>
		</div>
	</div>

	<div v-if="parseWarnings.length > 0" class="conversor-warnings">
		<VaAlert color="warning" border="left">
			<ul class="conversor-warnings__list">
				<li v-for="(warning, idx) in parseWarnings" :key="`${warning}-${idx}`">{{ warning }}</li>
			</ul>
		</VaAlert>
	</div>

	<VaDivider v-if="parseEjecutado" class="validador-divider" />

	<section v-if="parseEjecutado" class="validador-panel validador-panel--errors">
		<div class="validador-errors-header">
			<div class="validador-panel__header">
				<VaIcon name="table_rows" class="validador-panel__icon" />
				<div>
					<h2 class="validador-panel__title">Registros procesados</h2>
					<p class="validador-panel__hint">
						{{ isErrorFile ? 'Archivo de calidad de datos (DQ) con observaciones SEP' : 'Archivo TXT PUI estándar' }}
					</p>
				</div>
			</div>
			<VaBadge :text="String(registrosFiltrados.length)" color="primary" class="validador-errors-badge" />
		</div>

		<div class="conversor-search">
			<VaInput v-model="busqueda" placeholder="Buscar por nombre, CURP, matrícula o código de error..." clearable>
				<template #prependInner>
					<VaIcon name="search" size="small" />
				</template>
			</VaInput>
		</div>

		<div v-if="registrosFiltrados.length === 0" class="validador-empty validador-empty--idle">
			<VaIcon name="search_off" size="large" color="info" />
			<p class="validador-empty__title">Sin coincidencias</p>
			<p class="validador-empty__hint">Ajusta el criterio de búsqueda para ver registros.</p>
		</div>

		<div v-else class="validador-table-wrap">
			<table class="validador-errors-table conversor-table">
				<colgroup>
					<col style="width: 2%" />
					<col style="width: 24%" />
					<col style="width: 8%" />
					<col style="width: 12%" />
					<col style="width: 24%" />
					<col style="width: 30%" />
				</colgroup>
				<thead>
					<tr>
						<th scope="col">#</th>
						<th scope="col">Nombre</th>
						<th scope="col">CURP</th>
						<th scope="col">Matrícula</th>
						<th scope="col">Código de Error</th>
						<th scope="col">Mensaje de Error</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="record in registrosFiltrados" :key="`${record.rowIndex}-${record.curp}`">
						<td>{{ record.rowIndex }}</td>
						<td>{{ record.nombreCompleto }}</td>
						<td>
							<code class="validador-curp">{{ record.curp }}</code>
						</td>
						<td>{{ record.matricula }}</td>
						<td>
							<ul v-if="record.tieneError" class="conversor-error-list">
								<li v-for="codigo in record.codigosError" :key="`${record.rowIndex}-${codigo}`">
									<span class="validador-field-tag">
										<VaIcon name="report" class="validador-field-tag__icon" size="14px" />
										<span class="validador-field-tag__text">{{ codigo }}</span>
									</span>
								</li>
							</ul>
							<span v-else class="conversor-ok-tag">Sin error</span>
						</td>
						<td>
							<ul v-if="record.tieneError" class="conversor-error-list">
								<li v-for="codigo in record.codigosError" :key="`${record.rowIndex}-msg-${codigo}`">
									<span class="validador-field-tag">
										<VaIcon name="report" class="validador-field-tag__icon" size="14px" />
										<span class="validador-field-tag__text">{{ getCodigoErrorMensaje(codigo) }}</span>
									</span>
								</li>
							</ul>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</section>

	<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al procesar el archivo'" :test-mode="false" size="medium">
		<template #body>
			<div class="validador-modal validador-modal--error">
				<div class="validador-modal__header">
					<div class="validador-modal__icon validador-modal__icon--error">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="validador-modal__title">Error</h3>
				</div>
				<p class="validador-modal__text">{{ error || 'Error al procesar el archivo' }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.conversor-warnings {
	margin-top: 0.25rem;
}
.conversor-warnings__list {
	margin: 0;
	padding-left: 1.1rem;
}
.conversor-search {
	margin-bottom: 1rem;
}
.conversor-ok-tag {
	display: inline-flex;
	align-items: center;
	padding: 0.25rem 0.45rem;
	border-radius: 6px;
	font-size: 0.72rem;
	font-weight: 700;
	color: #14532d;
	background: #ecfdf5;
	border: 1px solid rgba(42, 122, 91, 0.25);
}
.conversor-error-list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
}
.conversor-error-list--messages {
	list-style: disc;
	padding-left: 1.1rem;
}
.conversor-error-list--messages li {
	font-size: 0.78rem;
	line-height: 1.35;
	color: #374151;
}
.validador-stat--warn {
	border-color: rgba(178, 31, 46, 0.25);
	background: #fef2f2;
}
.validador-stat--warn .validador-stat__value {
	color: #b21f2e;
}
.validador-grid {
	display: grid;
	grid-template-columns: minmax(280px, 1fr) minmax(320px, 1.1fr);
	gap: 1.25rem;
}
.validador-panel {
	border: 1px solid #e5e7eb;
	border-radius: 14px;
	padding: 1.25rem;
	background: #fff;
}
.validador-panel--info {
	background: #ffffff;
}
.validador-panel--upload {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}
.validador-panel__header {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	margin-bottom: 0.85rem;
}
.validador-panel--upload .validador-panel__header,
.validador-panel--errors .validador-panel__header {
	margin-bottom: 0;
}
.validador-panel__icon {
	color: #062342;
	margin-top: 0.1rem;
	flex-shrink: 0;
}
.validador-panel__title {
	margin: 0;
	font-size: 1rem;
	font-weight: 700;
	color: #062342;
}
.validador-panel__hint {
	margin: 0.2rem 0 0;
	font-size: 0.8rem;
	color: #6b7280;
}
.validador-panel__text {
	margin: 0 0 1rem;
	font-size: 0.875rem;
	line-height: 1.6;
	color: #4b5563;
}
.validador-steps {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.65rem;
}
.validador-step {
	display: flex;
	align-items: flex-start;
	gap: 0.65rem;
	font-size: 0.84rem;
	line-height: 1.45;
	color: #374151;
}
.validador-step__num {
	flex-shrink: 0;
	width: 1.5rem;
	height: 1.5rem;
	border-radius: 999px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 0.72rem;
	font-weight: 800;
	color: #062342;
	background: rgba(36, 200, 255, 0.18);
	border: 1px solid rgba(36, 200, 255, 0.35);
}
.validador-upload :deep(.va-file-upload__dropzone) {
	border-radius: 12px;
	border: 1px dashed rgba(6, 35, 66, 0.28);
	background: #f8fafc;
	min-height: 150px;
}
.validador-file-badge {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.65rem 0.85rem;
	border-radius: 10px;
	background: #f0fdf4;
	border: 1px solid rgba(42, 122, 91, 0.22);
}
.validador-file-badge__name {
	font-size: 0.84rem;
	font-weight: 600;
	color: #166534;
	word-break: break-all;
}
.validador-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	margin-top: auto;
}
.validador-stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.85rem;
	margin-top: 1.25rem;
}
.validador-stat {
	padding: 1rem;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	background: #f8fafc;
	text-align: center;
}
.validador-stat__value {
	display: block;
	font-size: 1.75rem;
	font-weight: 800;
	color: #062342;
	line-height: 1.1;
}
.validador-stat__label {
	display: block;
	margin-top: 0.35rem;
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}
.validador-divider {
	margin: 1.25rem 0 0;
}
.validador-errors-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1rem;
}
.validador-errors-badge {
	flex-shrink: 0;
}
.validador-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	gap: 0.5rem;
	padding: 2.5rem 1.5rem;
	border-radius: 12px;
	border: 1px dashed rgba(6, 35, 66, 0.16);
	background: #f8fafc;
}
.validador-empty__title {
	margin: 0.25rem 0 0;
	font-size: 1rem;
	font-weight: 700;
	color: #062342;
}
.validador-empty__hint {
	margin: 0;
	max-width: 440px;
	font-size: 0.875rem;
	color: #6b7280;
	line-height: 1.5;
}
.validador-table-wrap {
	border-radius: 12px;
	overflow: auto;
	border: 1px solid #e5e7eb;
	max-height: 420px;
	background: #fff;
}
.validador-errors-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}
.validador-errors-table thead th {
	position: sticky;
	top: 0;
	z-index: 1;
	padding: 0.55rem 0.75rem;
	background: #062342;
	color: #fff;
	font-weight: 700;
	font-size: 0.68rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	text-align: left;
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.validador-errors-table tbody td {
	padding: 0.5rem 0.75rem;
	vertical-align: top;
	border-bottom: 1px solid #eef2f7;
	color: #374151;
	font-size: 0.8125rem;
	line-height: 1.4;
	word-break: break-word;
}
.validador-errors-table tbody tr:nth-child(even) {
	background: #f8fafc;
}
.validador-errors-table tbody tr:hover {
	background: #f0f7ff;
}
.validador-curp {
	display: inline-block;
	font-size: 0.78rem;
	padding: 0.2rem 0.35rem;
	font-weight: 600;
	border-radius: 6px;
	background: #f1f5f9;
	color: #0f172a;
	word-break: break-all;
}
.validador-field-tag {
	display: inline-flex;
	align-items: flex-start;
	gap: 0.3rem;
	max-width: 100%;
	padding: 0.25rem 0.45rem;
	border-radius: 6px;
	font-size: 0.7rem;
	font-weight: 700;
	line-height: 1.25;
	color: #991b1b;
	background: #fef2f2;
	border: 1px solid #fecaca;
}
.validador-field-tag__icon {
	flex-shrink: 0;
	margin-top: 0.1rem;
	color: #dc2626;
}
.validador-field-tag__text {
	min-width: 0;
	word-break: break-word;
}
.validador-modal {
	border-radius: 14px;
	padding: 1.25rem;
}
.validador-modal--error {
	background: #fee2e2;
	border: 1px solid #fecaca;
}
.validador-modal__header {
	display: flex;
	align-items: center;
	gap: 0.85rem;
	margin-bottom: 0.85rem;
}
.validador-modal__icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
}
.validador-modal__icon--error {
	background: #b21f2e;
}
.validador-modal__title {
	margin: 0;
	font-size: 1.15rem;
	font-weight: 700;
	color: #991b1b;
}
.validador-modal__text {
	margin: 0;
	font-size: 0.95rem;
	line-height: 1.55;
	color: #374151;
}
@media (max-width: 960px) {
	.validador-grid,
	.validador-stats {
		grid-template-columns: 1fr;
	}
}
@media (max-width: 640px) {
	.validador-actions :deep(.va-button) {
		width: 100%;
	}
	.validador-errors-header {
		flex-direction: column;
	}
}
</style>
